import { APPLICANT_ATTRIBUTIONS, CUSTOMER_DATA_TABS } from 'cpool-constants';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AddExpense } from '../Expenses/AddExpense.tsx';
import { ExpensesApplicant } from '../Expenses/ExpensesApplicant.tsx';
import { useExpensesForm } from '../hooks/useExpensesForm.ts';
import { useIncomeForm } from '../hooks/useIncomeForm.ts';
import { AddIncome } from '../Income/AddIncome.tsx';
import { IncomeApplicant } from '../Income/IncomeApplicant.tsx';
import { hide } from '~/features/leadDetails/components/Overview/CustomerData/General/utils/hide.ts';
import { ApplicantCollapsible } from '~/features/leadDetails/components/Overview/CustomerData/shared/ApplicantCollapsible.tsx';
import { SectionHeading } from '~/features/leadDetails/components/Overview/CustomerData/shared/SectionHeading.tsx';
import { useCustomerDataTabsContext } from '~/features/leadDetails/context/CustomerDataTabsContext.tsx';
import { countRowErrorsForApplicant } from '~/features/leadDetails/utils/countRowErrorsForApplicant.ts';

type Props = {
    lead: SchemaLoanRequestResolver_Get;
};

const IncomeAndExpenses: React.FC<Props> = ({ lead }) => {
    const { t } = useTranslation();
    const { tab } = useCustomerDataTabsContext();
    const income = useIncomeForm(lead);
    const expenses = useExpensesForm(lead);
    const hasSecondApplicant = lead.antragsteller2 != null;
    const getApplicantErrorCount = (applicant: 1 | 2) => {
        const attribution =
            applicant === 1 ? APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_1 : APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_2;

        return (
            countRowErrorsForApplicant(income.methods.formState.errors, income.methods.getValues(), attribution) +
            countRowErrorsForApplicant(expenses.methods.formState.errors, expenses.methods.getValues(), attribution)
        );
    };

    const applicantContent = (applicant: 1 | 2) => (
        <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
            <section className="space-b-2 pb-4 bg-white rounded">
                <FormProvider {...income.methods}>
                    <SectionHeading action={<AddIncome applicant={applicant} />}>
                        {t('LEADS.DETAILS.INCOME.INCOME')}
                    </SectionHeading>
                    <IncomeApplicant applicant={applicant} onDelete={income.onDelete} />
                </FormProvider>
            </section>
            <section className="space-b-2 pb-4 bg-white rounded">
                <FormProvider {...expenses.methods}>
                    <SectionHeading action={<AddExpense applicant={applicant} />}>
                        {t('EXPENSES.HEADING')}
                    </SectionHeading>
                    <ExpensesApplicant applicant={applicant} onDelete={expenses.onDelete} />
                </FormProvider>
            </section>
        </div>
    );

    return (
        <div className="mb-2" style={hide(tab !== CUSTOMER_DATA_TABS.INCOME_AND_EXPENSES)}>
            <div className="space-y-2">
                <ApplicantCollapsible
                    applicant={1}
                    collapsible={hasSecondApplicant}
                    errorCount={getApplicantErrorCount(1)}
                    testId="income-expenses-applicant1"
                >
                    {applicantContent(1)}
                </ApplicantCollapsible>
                {hasSecondApplicant && (
                    <ApplicantCollapsible
                        applicant={2}
                        collapsible
                        errorCount={getApplicantErrorCount(2)}
                        style={hide(lead.coApplicantHidden)}
                        testId="income-expenses-applicant2"
                    >
                        {applicantContent(2)}
                    </ApplicantCollapsible>
                )}
            </div>
            {lead.coApplicantHidden && (
                <div className="flex justify-center my-6">
                    <span className="text-gray-400">{t('OVERVIEW.SECOND_APPLICANT_HIDDEN')}</span>
                </div>
            )}
        </div>
    );
};

export default IncomeAndExpenses;
